// import modules
import { APP_INITIALIZER, ErrorHandler, Injector, NgModule } from '@angular/core';
import { HTTP_INTERCEPTORS, HttpHeaders, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule } from '@angular/forms';
import { ColorPickerComponent, ColorPickerDirective } from 'ngx-color-picker';
import { UIRouterModule, UrlService, StateService, Transition, RejectType } from '@uirouter/angular';

import { SharedModule } from '@fi-shared/shared.module';

import { UtilitiesModule } from './utilities/utilities.module';
import { WidgetModule } from './widget/widget.module';
import { UserModule } from '@fi-app/user/user.module';
import { RoleModule } from './role/role.module';
import { BusinessUnitModule } from './business-unit/business-unit.module';
import { BusinessAreaModule } from './business-area/business-area.module';
import { AreaModule } from './area/area.module';
import { ClusterModule } from './cluster/cluster.module';
import { TagModule } from './tag/tag.module';
import { SmsModule } from './sms/sms.module';
import { MediaBankModule } from './media-bank/media-bank.module';

import { FranchiseIntranetUpgradeModule } from './franchise-intranet-upgrade/franchise-intranet-upgrade.module';
import { AgreementModule } from './agreement/agreement.module';
import { RequestAccessSettingModule } from './request-access-setting/request-access-setting.module';
import { DocumentModule } from './document/document.module';
import { ComponentsModule } from './components/components.module';
import { NewsFeedModule } from './news-feed/news-feed.module';
import { ForumModule } from './forum/forum.module';
import { PersonalNotificationsModule } from './personal-notifications/personal-notifications.module';
import { SiteMapModule } from './site-map/site-map.module';
import { FiHttpAuthInterceptor } from './utilities/http-auth-interceptor';

import { FiHttpInactivityCountdownInterceptor } from './utilities/http-inactivity-countdown-interceptor';
import { SystemNotificationsModule } from './system-notifications/system-notifications.module';
import { CoreModule } from './core/core.module';
import { FiAppearanceModule } from './appearance/appearance.module';
import { DataFieldModule } from './data-field/data-field.module';
import { ChecklistModule } from './checklist/checklist.module';
import { DocumentVersionModule } from './document-version/document-version.module';
import { ChecklistListModule } from './checklist/list/checklist-list.module';
import { TemplateModule } from './template/template.module';
import { ChecklistReportModule } from './checklist-report/checklist-report.module';
import { WidgetSettingsModule } from './widget-settings/widget-settings.module';
import { FiAuthenticateModule } from './authenticate/authenticate.module';
import { ExtendedSearchModule } from './extended-search/extended-search.module';
import { ChecklistTemplateModule } from './checklist-template/checklist-template.module';
import { FiDocumentEditModule } from './document/edit/edit.module';
import { FiHttpInterceptorCache } from '@fi-shared/fi-backend/http-interceptor-cache';
import './utilities/fi-services/fi-promise-finally';
import { GlobalSettingsModule } from './global-settings/global-settings.module';
import { BillingModule } from './billing/billing.module';
import { CustomerBillingModule } from './customer-billing/customer-billing.module';
import { OtherSettingsModule } from './other-settings/other-settings.module';
import { ImportModule } from './import/import.module';
import { FiHomeModule } from './home/home.module';
import { FiDocumentLayoutModule } from './document/document-layout/document-layout.module';
import { FiErrorHandler } from './utilities/fi-error-handler';
import { FiBackendService } from '@fi-shared/fi-backend/fi-backend.service';
import { CcmSessionData } from '@fi-app/components/core-base';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FileTypeService } from '@fi-shared/fi-shared-services/file-type.service';
import { SessionService } from '@fi-app/utilities/fi-services/session.service';
import { FiAppComponent } from '@fi-app/fi-app.component';
import { AuthenticateService } from '@fi-app/utilities/fi-services/authenticate.service';
import { FiAppInitComponent } from '@fi-app/fi-app-init.component';
import { FiStateChangeService } from '@fi-shared/fi-state-proxy/fi-state-change.service';
import { ErrorMessageService } from '@fi-app/utilities/fi-services/error-message.service';
import { FI_BACKEND_CONFIG } from '@fi-shared/fi-backend/fi-backend.service';
import { environment } from '../environments/environment';
import { getObjectTypeNameTextId } from '@fi-shared/utils/object-type-name';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { StateAccessCheckService } from '@fi-shared/fi-shared-services/state-access-check.service';
import { AppTextService } from '@fi-app/shared/app-text/app-text.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { HistoryModule } from '@fi-app/history/history.module';
import { FiDialogsModule, FiDialogsService } from '@fi-shared/fi-dialogs';
import { FiSharedServicesModule } from '@fi-shared/fi-shared-services';
import { registerForumStateRules } from '@fi-app/forum/forum-states';
import { registerCoreStateRules } from '@fi-app/core/core-states';
import { registerAgreementStateRules } from '@fi-app/agreement/agreement-states';
import { registerAppearanceStateRules } from '@fi-app/appearance/appearance-states';
import { registerADStateRules } from '@fi-app/external-platform-integration/ad-integration/ad-integration-states';
import { registerBillingStateRules } from '@fi-app/billing/billing-states';
import { registerChecklistStateRules } from '@fi-app/checklist/checklist-states';
import { registerDataFieldStateRules } from '@fi-app/data-field/data-field-states';
import { registerDocumentTemplateStateRules } from '@fi-app/document-template/states/document-template-module-states';
import { registerDocumentStateRules } from '@fi-app/document/states/document-module-state';
import { registerUpgradeStateRules } from '@fi-app/franchise-intranet-upgrade/franchise-intranet-upgrade-states';
import { registerHistoryStateRules } from '@fi-app/history/history-states';
import { registerImportStateRules } from '@fi-app/import/import-states';
import { registerMediaBankStateRules } from '@fi-app/media-bank/media-bank-states';
import { registerSiteMapStateRules } from '@fi-app/site-map/site-map-states';
import { registerSMSStateRules } from '@fi-app/sms/sms-states';
import { registerTagStateRules } from '@fi-app/tag/tag-states';
import { Statistic2Module } from '@fi-app/statistic2/statistic2.module';
import { registerStatistic2StateRules } from '@fi-app/statistic2/statistic2-states';
import { FiFormModule } from '@fi-app/fi-form/fi-form.module';
import { registerFormStateRules } from '@fi-app/fi-form/fi-form-states';
import { registerFormReportStateRules } from '@fi-app/fi-form/fi-form-report/fi-form-report-states';
import { UserRequestsAccessModule } from './user/fi-user-requests-list/fi-user-requests.module';
import { FiSnackbarModule } from './shared/fi-snackbar/fi-snackbar.module';
import { TaskModule } from '@fi-app/task/task.module';
import { taskHandleStateChange, registerTaskStateRules } from '@fi-app/task/task-states';
import { FiCustomerDataService, IFiCustomerData } from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { FiUserDataService, IFiUserData } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { PlatformShutDownNotificationService } from '@fi-app/utilities/notification/platform-shut-down-notification/platform-shut-down-notification.service';
import { PageTitleService } from '@fi-app/utilities/fi-services/page-title.service';
import { registerNewForumStateRules } from '@fi-shared/fi-forum/fi-forum-states';
import { FiChatModule } from '@fi-shared/fi-chat/fi-chat.module';
import { FiAiChatModule } from '@fi-shared/fi-ai-chat/fi-ai-chat.module';
import { FiClientGuidInterceptor } from '@fi-shared/fi-interceptors/fi-client-guid-interceptor';
import { registerOtherSettingsStateRules } from '@fi-app/other-settings/other-settings-states';
import { registerRoleStateRules } from '@fi-app/role/role-states';
import { registerBusinessAreaStateRules } from '@fi-app/business-area/business-area-states';
import { FiStateNames } from './shared/fi-state-proxy/fi-state-names';
import { registerPkStateRules } from '@fi-app/external-platform-integration/pk/pk-states';
import { registerChecklistV2StateRules } from '@fi-app/checklist/checklist-v2/checklist-v2.states';
import { FiTranslationModule } from '@fi-app/fi-translation/fi-translation.module';
import { FiSiteDesignModule } from '@fi-app/fi-site-design/fi-site-design.module';
import { ExternalPlatformIntegrationModule } from '@fi-app/external-platform-integration/external-platform-integration.module';
import { registerBalooStateRules } from '@fi-app/external-platform-integration/baloo/baloo-states';
import { FiClientTypeInterceptor } from '@fi-shared/fi-interceptors/fi-client-type-interceptor';
import { registerChecklistV2ReportStateRules } from '@fi-app/checklist/checklist-v2/checklist-v2-report-states';
import { registerPlandayStateRules } from '@fi-app/external-platform-integration/planday/planday-states';
import { registerChecklistV2AuditStateRules } from '@fi-app/checklist/checklist-v2/checklist-v2-audit.states';
import { registerChecklistV2AuditReportStateRules } from '@fi-app/checklist/checklist-v2/checklist-v2-audit-report-states';
import { registerTransferOwnershipStateRules } from '@fi-app/other-settings/transfer-ownership/transfer-ownership-states';
import { registerTaskV2StateRules } from '@fi-app/task-v2/task-v2-states';
import { TaskV2Module } from '@fi-app/task-v2/task-v2.module';
import { registerTaskV2ReportStateRules } from '@fi-app/task-v2/task-v2-report-states';
import { registerLoginReminderStateRules } from '@fi-app/other-settings/fi-login-reminder/fi-login-reminder-states';
import { FiOnboardingModule } from '@fi-app/fi-onboarding/fi-onboarding.module';
import { registerOnboardingStateRules } from '@fi-app/fi-onboarding/fi-onboarding-states';
import { registerOnboardingReportStateRules } from '@fi-app/fi-onboarding/fi-onboarding-report/fi-onboarding-report-states';

@NgModule({
    declarations: [FiAppComponent, FiAppInitComponent],
    bootstrap: [FiAppComponent],
    imports: [
        BrowserModule,
        BrowserAnimationsModule,
        FormsModule,
        ColorPickerComponent,
        ColorPickerDirective,
        UIRouterModule.forRoot({
            otherwise: { state: FiStateNames.Home._name },
            deferIntercept: true,
            deferInitialRender: true,
            config: UIRouterModule_config,
        }),
        SharedModule,
        FiSharedServicesModule,
        UtilitiesModule,
        WidgetModule,
        CoreModule,
        UserModule,
        RoleModule,
        BusinessUnitModule,
        BusinessAreaModule,
        AreaModule,
        ClusterModule,
        TagModule,
        SmsModule,
        FranchiseIntranetUpgradeModule,
        AgreementModule,
        FiAppearanceModule,
        MediaBankModule,
        RequestAccessSettingModule,
        UserRequestsAccessModule,
        DocumentModule,
        TemplateModule,
        NewsFeedModule,
        ForumModule,
        ComponentsModule,
        PersonalNotificationsModule,
        SiteMapModule,
        DataFieldModule,
        SystemNotificationsModule,
        ChecklistModule,
        ChecklistListModule,
        ChecklistTemplateModule,
        DocumentVersionModule,
        ChecklistReportModule,
        WidgetSettingsModule,
        FiAuthenticateModule,
        ExtendedSearchModule,
        FiDocumentEditModule,
        GlobalSettingsModule,
        BillingModule,
        CustomerBillingModule,
        OtherSettingsModule,
        ImportModule,
        FiHomeModule,
        FiDocumentLayoutModule,
        FiDialogsModule,
        FiSnackbarModule,
        HistoryModule,
        Statistic2Module,
        FiFormModule,
        TaskModule,
        ExternalPlatformIntegrationModule,
        FiSiteDesignModule,
        FiChatModule,
        FiAiChatModule,
        FiTranslationModule,
        TaskV2Module,
        FiOnboardingModule,
    ],
    providers: [
        {
            provide: FI_BACKEND_CONFIG,
            useFactory: (userDataService: FiUserDataService) => {
                let domain = environment.backend;
                if (!domain.startsWith('http://') && !domain.startsWith('https://')) {
                    domain = document.location.origin + domain;
                }
                return {
                    basePath: domain,
                    getToken: () => userDataService.getToken(),
                    fallbackBasePath: domain,
                };
            },
            deps: [FiUserDataService],
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FiHttpAuthInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FiHttpInactivityCountdownInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FiHttpInterceptorCache,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FiClientGuidInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: FiClientTypeInterceptor,
            multi: true,
        },
        {
            provide: ErrorHandler,
            useClass: FiErrorHandler,
        },
        {
            provide: APP_INITIALIZER,
            useFactory: app_init,
            multi: true,
            deps: [
                Injector,
                FiBackendService,
                ContentLanguagesService,
                FileTypeService,
                SessionService,
                AppTextService,
                UrlService,
            ],
        },
        provideHttpClient(withInterceptorsFromDi()),
    ],
})
export class FiAppModule {}

export function app_init(injector: Injector) {
    injector.get(ErrorMessageService).subscribeOnBackendErrors();
    return () => {
        const promise = new Promise<void>((resolve, reject) => {
            app_init_customer_data(injector).then(() => {
                app_init_check_token(injector).then((userData) => {
                    app_init_check_user_data_result(injector, userData);

                    app_init_language(injector).then(() => {
                        injector.get(UrlService).sync();
                        initStateWatchers(injector);
                        resolve();
                    }, reject);
                }, reject);
            }, reject);
        });

        promise.catch(() => {
            document.querySelector('#main_loading').className += ' ng-hide';
        });
        return promise;
    };
}

function app_init_customer_data(injector: Injector) {
    injector.get(SessionService).loadFromLocalStorage();

    let language;
    const navigator: any = window.navigator;
    if (navigator.languages) {
        language = navigator.languages[0];
    } else {
        language = navigator.userLanguage || navigator.language;
    }

    return injector
        .get(FiBackendService)
        .fetchData<IFiCustomerData>('Core/Authenticate/GetDataForDomain', {
            forceShowError: true,
            reqConfig: {
                headers: new HttpHeaders({
                    'Accept-Language': language || 'en-US',
                }),
            },
        })
        .then((data) => {
            injector.get(FiCustomerDataService).setCustomerData(data);

            injector.get(SessionService).setSiteDesign(data.SiteDesign);

            //return CcmSettingsData;
        });
}

function app_init_language(injector: Injector) {
    return injector
        .get(AppTextService)
        .loadApplicationLanguageLabels(injector.get(FiUserDataService).applicationLanguage);
}

function app_init_check_token(injector: Injector) {
    return new Promise<IFiUserData>((resolve, reject) => {
        let token = urlParam('token');
        if (token) {
            // redirecting from mobile app
        } else {
            token = injector.get(FiUserDataService).getToken();
        }

        const authenticateService = injector.get(AuthenticateService);
        authenticateService
            .IsTokenValid(token)
            .then((isValid) => {
                if (isValid) {
                    injector.get(FiUserDataService).updateCurrentBrowserTabInfo(token);

                    authenticateService
                        .GetUserData()
                        .then((userData) => {
                            if (userData) {
                                resolve(userData);
                            } else {
                                resolve(null);
                            }
                        })
                        .catch(reject);
                } else {
                    resolve(null);
                }
            })
            .catch(reject);
    });
}

function app_init_check_user_data_result(injector: Injector, userData: IFiUserData) {
    const sessionService = injector.get(SessionService),
        userDataService = injector.get(FiUserDataService);
    userDataService.onUserDataChanged$.subscribe(() => {
        injector.get(PlatformShutDownNotificationService).checkShutOffDate();
    });

    if (userData) {
        userDataService.setUserData(userData);
    } else {
        sessionService.resetSession();
    }
}

function initStateWatchers(injector: Injector) {
    let attemptLoginData: {
        stateName: string;
        stateParams: any;
    };

    const stateService = injector.get(StateService),
        stateChangeService = injector.get(FiStateChangeService),
        stateAccessCheckService = injector.get(StateAccessCheckService),
        appTextService = injector.get(AppTextService),
        fiDialogsService = injector.get(FiDialogsService),
        customerDataService = injector.get(FiCustomerDataService),
        userDataService = injector.get(FiUserDataService),
        fiLoading = injector.get(LoadingService),
        sessionService = injector.get(SessionService);

    stateChangeService.onStart$.subscribe((tr: Transition) => {
        sessionService.toggleDashboard(false);

        const newState = tr.to(),
            newParams = tr.params();

        if (!userDataService.authorized && newState.name.indexOf('Login.') !== 0 && newState.name !== 'LoginV2') {
            attemptLoginData = {
                stateName: newState.name,
                stateParams: newParams,
            };
            tr.abort();
            stateService.go('Login.Auth');
        } else if (userDataService.authorized) {
            if (attemptLoginData && attemptLoginData.stateName !== newState.name) {
                const tempData = attemptLoginData;
                tr.abort();
                stateService.go(tempData.stateName, tempData.stateParams);
            } else if (!stateAccessCheckService.isStateAllowedForCurrentUser(newState.name, newParams)) {
                tr.abort();
                // show 'state not allowed' warning if we have object type for state
                const objectType = stateAccessCheckService.getObjectTypeForState(newState.name);
                if (objectType) {
                    const objectTypeName = appTextService.get(getObjectTypeNameTextId(objectType)).toLowerCase();
                    const textId =
                        objectType === ObjectTypes.Role
                            ? 'mediaModule.delete_dialog_no_rights_denna'
                            : 'mediaModule.delete_dialog_no_rights';
                    fiDialogsService.error({
                        messageText: appTextService.get(textId).replace('{x}', objectTypeName),
                    });
                } else {
                    fiDialogsService.error({
                        messageText: appTextService.get('error_message.no_access_to_action'),
                    });
                    stateService.go(FiStateNames.Home._name);
                }
            } else {
                fiLoading.startLoading();
            }
            attemptLoginData = undefined;
        }
    });

    stateChangeService.onFinish$.subscribe((tr) => {
        if (customerDataService.isTestCustomer()) {
            // info for flutter
            console.log('current state:', tr.to().name, tr.params());
        }
        fiLoading.finishLoading();
    });

    stateChangeService.onError$.subscribe((transition) => {
        const error = transition.error();
        if (error.type === RejectType.ERROR) {
            console.error('State change error ', error);
            fiLoading.finishLoading();
        }
    });
}

function urlParam(name) {
    var results = new RegExp('[\?&]' + name + '=([^&#]*)').exec(window.location.href);
    if (results == null) {
        return null;
    } else {
        return decodeURI(results[1]) || '';
    }
}

export function UIRouterModule_config(uiRouterInstance, injector: Injector) {
    const fiStateChangeService = injector.get(FiStateChangeService);
    fiStateChangeService.initTransitionHooks(uiRouterInstance.transitionService);

    const stateAccessCheckService = injector.get(StateAccessCheckService);
    const userDataService = injector.get(FiUserDataService);
    registerCoreStateRules(stateAccessCheckService, userDataService);
    registerRoleStateRules(stateAccessCheckService);
    registerBusinessAreaStateRules(stateAccessCheckService);
    registerAgreementStateRules(stateAccessCheckService);
    registerAppearanceStateRules(stateAccessCheckService);
    registerADStateRules(stateAccessCheckService);
    registerBillingStateRules(stateAccessCheckService);
    registerChecklistStateRules(stateAccessCheckService);
    registerChecklistV2StateRules(stateAccessCheckService);
    registerChecklistV2ReportStateRules(stateAccessCheckService);
    registerChecklistV2AuditStateRules(stateAccessCheckService);
    registerChecklistV2AuditReportStateRules(stateAccessCheckService);
    registerDataFieldStateRules(stateAccessCheckService);
    registerDocumentTemplateStateRules(stateAccessCheckService);
    registerDocumentStateRules(stateAccessCheckService);
    registerUpgradeStateRules(stateAccessCheckService);
    registerHistoryStateRules(stateAccessCheckService);
    registerImportStateRules(stateAccessCheckService);
    registerMediaBankStateRules(stateAccessCheckService);
    registerSiteMapStateRules(stateAccessCheckService);
    registerSMSStateRules(stateAccessCheckService);
    registerTagStateRules(stateAccessCheckService);
    registerOtherSettingsStateRules(stateAccessCheckService);
    registerForumStateRules(stateAccessCheckService);
    registerStatistic2StateRules(stateAccessCheckService);
    registerFormStateRules(stateAccessCheckService);
    registerFormReportStateRules(stateAccessCheckService);
    registerTaskStateRules(stateAccessCheckService);
    registerPkStateRules(stateAccessCheckService);
    registerBalooStateRules(stateAccessCheckService);
    registerPlandayStateRules(stateAccessCheckService);
    registerNewForumStateRules(stateAccessCheckService);
    registerTransferOwnershipStateRules(stateAccessCheckService);
    registerTaskV2StateRules(stateAccessCheckService);
    registerTaskV2ReportStateRules(stateAccessCheckService);
    registerLoginReminderStateRules(stateAccessCheckService);
    registerOnboardingStateRules(stateAccessCheckService);
    registerOnboardingReportStateRules(stateAccessCheckService);

    fiStateChangeService.onSuccess$.subscribe((tr) => {
        taskHandleStateChange(tr, injector.get(PageTitleService));
    });
}
